import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { useAuth, useUser } from "@clerk/react";
import type { DisplayMode, SourceSnapshot } from "@tuit/shared-types";
import {
  adminCatalogGet,
  adminCatalogPatch,
  adminCatalogFeed,
  adminCatalogFeedRaw,
  adminCatalogFavicon,
} from "../api.js";
import { getFaviconUrl } from "../utils/favicon.js";
import { FlatView } from "../components/FlatView.js";
import { SourcePanel } from "../components/SourcePanel.js";
import { PhoneFrame } from "../components/PhoneFrame.js";
import { PHONE_MODELS } from "../utils/phoneModels.js";
import type { PhoneModel } from "../utils/phoneModels.js";
import styles from "../styles/pages/FeedEditorPage.module.css";

interface CatalogSource {
  id: string;
  title: string;
  feedUrl: string;
  htmlUrl: string | null;
  faviconUrl: string | null;
  description: string | null;
  category: string;
  status: "pending" | "approved" | "rejected";
  thumbnails: boolean;
  language: string;
  tags: string[];
}

interface FeedItem {
  title: string;
  link: string | null;
  pubDate: string | null;
  summary: string | null;
  thumbnail: string | null;
}

type PreviewMode = "post" | "thumbnail" | "gallery" | "picture" | "json" | "raw-json";

const PREVIEW_MODES: { mode: PreviewMode; label: string }[] = [
  { mode: "post",      label: "Post" },
  { mode: "thumbnail", label: "Thumbnail" },
  { mode: "gallery",   label: "Gallery" },
  { mode: "picture",   label: "Picture Set" },
];

const DISPLAY_MODES: DisplayMode[] = ["text", "picture", "gallery"];

const ICON_BG_OPTIONS: Array<{ label: string; value: string }> = [
  { label: "Transparent", value: "" },
  { label: "White",       value: "#ffffff" },
  { label: "87% white",   value: "#dedede" },
  { label: "75% white",   value: "#bfbfbf" },
  { label: "62% white",   value: "#9e9e9e" },
  { label: "50% grey",    value: "#808080" },
  { label: "37% black",   value: "#5e5e5e" },
  { label: "25% black",   value: "#404040" },
  { label: "Black",       value: "#000000" },
];

const ALL_TAGS = [
  { id: "nsfw",        label: "NSFW" },
  { id: "satire",      label: "Satire" },
  { id: "opinion",     label: "Opinion" },
  { id: "longform",    label: "Longform" },
  { id: "paywall",     label: "Paywall" },
  { id: "video",       label: "Video" },
  { id: "podcast",     label: "Podcast" },
  { id: "newsletter",  label: "Newsletter" },
  { id: "aggregator",  label: "Aggregator" },
  { id: "official",    label: "Official" },
  { id: "community",   label: "Community" },
  { id: "academic",    label: "Academic" },
];

function toSnapshot(
  items: FeedItem[],
  sourceId: string,
  titleStr: string,
  stripThumbs: boolean,
  faviconUrl?: string | null,
  iconBackground?: string,
): SourceSnapshot {
  return {
    sourceId,
    title: titleStr,
    fetchedAt: new Date().toISOString(),
    fetchStatus: "ok",
    errorMessage: null,
    faviconUrl: faviconUrl || null,
    iconBackground: iconBackground || undefined,
    items: items.map((item, i) => {
      let domain = "";
      try { domain = new URL(item.link ?? "").hostname.replace(/^www\./, ""); } catch {}
      return {
        id: `preview-${i}`,
        title: item.title,
        url: item.link ?? "#",
        domain,
        author: null,
        score: null,
        publishedAt: item.pubDate ?? new Date().toISOString(),
        thumbnail: stripThumbs ? null : item.thumbnail,
        duplicateOf: null,
      };
    }),
  };
}

const EMPTY_SET = new Set<string>();
const NOOP = () => {};

function htmlEscape(s: string) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function syntaxHighlight(json: string): string {
  return htmlEscape(json).replace(
    /("(?:\\u[0-9a-fA-F]{4}|\\[^u]|[^\\"])*"(?:\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      const isKey = /^".*"\s*:$/.test(match);
      const isStr = match.startsWith('"') && !isKey;
      const isBool = match === "true" || match === "false";
      const isNull = match === "null";
      const color = isKey ? "#93c5fd" : isStr ? "#86efac" : isBool ? "#f9a8d4" : isNull ? "#94a3b8" : "#fca5a5";
      return `<span style="color:${color}">${match}</span>`;
    },
  );
}

export function FeedEditorPage() {
  const { sourceId } = useParams({ strict: false }) as { sourceId: string };
  const { isLoaded, userId } = useAuth();
  const navigate = useNavigate();
  const { user } = useUser();
  const role = (user?.publicMetadata as { role?: string } | undefined)?.role;
  const isAdmin = isLoaded && !!userId && (role === "admin" || role === "editor");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [catalogSource, setCatalogSource] = useState<CatalogSource | null>(null);

  // Catalog fields
  const [title, setTitle] = useState("");
  const [feedUrl, setFeedUrl] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [htmlUrl, setHtmlUrl] = useState("");
  const [faviconUrl, setFaviconUrl] = useState("");
  const [language, setLanguage] = useState("en");
  const [thumbnails, setThumbnails] = useState(false);
  const [tags, setTags] = useState<string[]>([]);

  // SourceConfig fields
  const [iconBackground, setIconBackground] = useState("");
  const [displayMode, setDisplayMode] = useState<DisplayMode>("text");
  const [refreshIntervalMinutes, setRefreshIntervalMinutes] = useState(360);

  const [faviconFetching, setFaviconFetching] = useState(false);

  // Feed preview
  const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
  const [feedMeta, setFeedMeta] = useState<{ feedTitle: string | null; feedLink: string | null; feedDescription: string | null } | null>(null);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedError, setFeedError] = useState<string | null>(null);
  const [rawFeedData, setRawFeedData] = useState<unknown>(null);
  const [rawFeedLoading, setRawFeedLoading] = useState(false);
  const [rawFeedError, setRawFeedError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<PreviewMode>("thumbnail");
  const [previewCols, setPreviewCols] = useState(2);
  const [previewMobile, setPreviewMobile] = useState(false);
  const [phoneModel, setPhoneModel] = useState<PhoneModel>(PHONE_MODELS[0]!);

  // Save
  const [saving, setSaving] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoaded && !isAdmin) navigate({ to: "/" });
  }, [isLoaded, isAdmin]);

  function fetchFeedItems(url: string, onData?: (d: { feedLink: string | null; feedDescription: string | null }) => void) {
    setFeedItems([]);
    setFeedMeta(null);
    setFeedError(null);
    setFeedLoading(true);
    adminCatalogFeed(url)
      .then((data) => {
        setFeedItems(data.items);
        setFeedMeta({ feedTitle: data.feedTitle, feedLink: data.feedLink, feedDescription: data.feedDescription });
        onData?.(data);
      })
      .catch((err) => setFeedError(String(err)))
      .finally(() => setFeedLoading(false));
  }

  function fetchRaw(url: string) {
    setRawFeedData(null);
    setRawFeedError(null);
    setRawFeedLoading(true);
    adminCatalogFeedRaw(url)
      .then((data) => setRawFeedData(data))
      .catch((err) => setRawFeedError(String(err)))
      .finally(() => setRawFeedLoading(false));
  }

  async function fetchFavicon(targetUrl: string) {
    setFaviconFetching(true);
    try {
      const data = await adminCatalogFavicon(targetUrl);
      if (data.faviconUrl) setFaviconUrl(data.faviconUrl);
    } catch { /* ignore */ } finally {
      setFaviconFetching(false);
    }
  }

  useEffect(() => {
    if (!isLoaded || !isAdmin) return;
    setLoading(true);
    setLoadError(null);
    setSaveSuccess(false);
    setSaveError(null);

    Promise.all([
      adminCatalogGet(sourceId),
      fetch(`/api/source/${sourceId}/config`).then((r) => r.ok ? r.json() : null),
    ]).then(([catalogRaw, configRaw]) => {
      const s = catalogRaw as CatalogSource;
      setCatalogSource(s);
      setTitle(s.title);
      setFeedUrl(s.feedUrl);
      setCategory(s.category);
      setDescription(s.description ?? "");
      setHtmlUrl(s.htmlUrl ?? "");
      setFaviconUrl(s.faviconUrl ?? "");
      setLanguage(s.language ?? "en");
      setThumbnails(s.thumbnails);
      setTags(s.tags ?? []);

      const cfg = configRaw as { iconBackground?: string; displayMode?: DisplayMode; refreshIntervalMinutes?: number } | null;
      setIconBackground(cfg?.iconBackground ?? "");
      setDisplayMode(cfg?.displayMode ?? "text");
      setRefreshIntervalMinutes(cfg?.refreshIntervalMinutes ?? 360);

      fetchFeedItems(s.feedUrl, (data) => {
        if (!s.htmlUrl && data.feedLink) setHtmlUrl(data.feedLink);
        if (!s.description && data.feedDescription) setDescription(data.feedDescription);
      });
      if (!s.faviconUrl) fetchFavicon(s.htmlUrl || s.feedUrl);
    }).catch((err) => {
      setLoadError(err instanceof Error ? err.message : "Failed to load source");
    }).finally(() => setLoading(false));
  }, [sourceId, isLoaded, isAdmin]);

  async function handleSave() {
    if (!catalogSource || saving) return;
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      await adminCatalogPatch(catalogSource.id, {
        title,
        feedUrl: feedUrl || undefined,
        category,
        description: description || null,
        htmlUrl: htmlUrl || null,
        faviconUrl: faviconUrl || null,
        language,
        thumbnails,
        tags,
      });

      const configRes = await fetch(`/api/source/${sourceId}/config`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, url: feedUrl, thumbnails, displayMode, iconBackground, refreshIntervalMinutes, faviconUrl: faviconUrl || null }),
      });
      if (!configRes.ok) {
        const err = await configRes.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? `Config save failed: HTTP ${configRes.status}`);
      }

      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed");
      setSaving(false);
      return;
    }
    setSaving(false);

    setRebuilding(true);
    try {
      await fetch(`/api/source/${sourceId}/snapshot`, { method: "DELETE" });
      await fetch(`/api/source/${sourceId}/snapshot`);
    } catch { /* snapshot rebuild failure is non-fatal */ } finally {
      setRebuilding(false);
      fetchFeedItems(feedUrl);
    }
  }

  function toggleTag(tag: string) {
    setTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
  }

  if (!isLoaded || !isAdmin) return null;

  const headerRight = rebuilding
    ? <span className={styles.statusNote}>Publishing…</span>
    : saveSuccess
    ? <span className={`${styles.statusNote} ${styles.statusNoteSuccess}`}>Saved</span>
    : null;

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <Link to="/admin/feeds" className={styles.back}>← Feeds</Link>
          <code className={styles.sourceId}>{sourceId}</code>
        </div>
        <div className={styles.loadingState}>Loading…</div>
      </div>
    );
  }

  if (loadError || !catalogSource) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <Link to="/admin/feeds" className={styles.back}>← Feeds</Link>
        </div>
        <div className={styles.loadingState}>{loadError ?? "Source not found."}</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Link to="/admin/feeds" className={styles.back}>← Feeds</Link>
        <code className={styles.sourceId}>{sourceId}</code>
        <span className={`${styles.statusBadge} ${styles[`status_${catalogSource.status}`]}`}>
          {catalogSource.status}
        </span>
        {headerRight}
      </div>

      <div className={styles.layout}>
        {/* ── Left: form ── */}
        <div className={styles.sidebar}>
          <div className={styles.form}>
            <div className={styles.sourceUrl}>
              <div className={styles.feedUrlRow}>
                <input
                  className={styles.input}
                  type="url"
                  value={feedUrl}
                  onChange={(e) => setFeedUrl(e.target.value)}
                  style={{ flex: 1, fontSize: 11 }}
                />
                <button
                  type="button"
                  className={styles.fetchBtn}
                  onClick={() => fetchFeedItems(feedUrl)}
                  disabled={feedLoading || !feedUrl}
                  title="Re-fetch with this URL"
                >
                  {feedLoading ? "…" : "↻"}
                </button>
                <a href={feedUrl} target="_blank" rel="noopener noreferrer" className={styles.feedUrlLink} title="Open feed URL">↗</a>
              </div>
            </div>

            <label className={styles.label}>
              Title
              <input className={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>

            <label className={styles.label}>
              Category
              <input className={styles.input} value={category} onChange={(e) => setCategory(e.target.value)} />
            </label>

            <label className={styles.label}>
              Description
              <textarea className={styles.textarea} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>

            <label className={styles.label}>
              Homepage URL
              <input className={styles.input} type="url" value={htmlUrl} onChange={(e) => setHtmlUrl(e.target.value)} />
            </label>

            <div className={styles.label}>
              Favicon URL
              <div className={styles.faviconRow}>
                <input
                  className={styles.input}
                  type="url"
                  value={faviconUrl}
                  onChange={(e) => setFaviconUrl(e.target.value)}
                  placeholder={faviconFetching ? "Fetching…" : "Auto-fetch or paste URL"}
                  disabled={faviconFetching}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className={styles.fetchBtn}
                  onClick={() => fetchFavicon(htmlUrl || feedUrl)}
                  disabled={faviconFetching || (!htmlUrl && !feedUrl)}
                >
                  {faviconFetching ? "…" : "Fetch"}
                </button>
              </div>
            </div>

            <label className={styles.label}>
              Language
              <input className={styles.input} value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="en" style={{ width: 80 }} />
            </label>

            <label className={styles.label}>
              Refresh (min)
              <input
                className={styles.input}
                type="number"
                min={1}
                value={refreshIntervalMinutes}
                onChange={(e) => setRefreshIntervalMinutes(Number(e.target.value))}
                style={{ width: 100 }}
              />
            </label>

            <label className={styles.checkLabel}>
              <input type="checkbox" checked={thumbnails} onChange={(e) => setThumbnails(e.target.checked)} />
              Show thumbnails
            </label>

            <div className={styles.label}>
              Display Mode
              <div className={styles.displayModeToggle}>
                {DISPLAY_MODES.map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`${styles.displayModeBtn} ${displayMode === m ? styles.displayModeBtnActive : ""}`}
                    onClick={() => setDisplayMode(m)}
                  >
                    {m.charAt(0).toUpperCase() + m.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.label}>
              Icon BG
              <div className={styles.iconBgSwatches}>
                {ICON_BG_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    title={opt.label}
                    className={`${styles.iconBgSwatch} ${iconBackground === opt.value ? styles.iconBgSwatchActive : ""}`}
                    style={opt.value ? { background: opt.value } : undefined}
                    onClick={() => setIconBackground(opt.value)}
                  >
                    {!opt.value && <span className={styles.iconBgTransparentMark}>✕</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.tagSection}>
              <span className={styles.tagSectionLabel}>Tags</span>
              <div className={styles.tagGrid}>
                {ALL_TAGS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`${styles.tagBtn} ${tags.includes(t.id) ? styles.tagBtnActive : ""}`}
                    onClick={() => toggleTag(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.footer}>
            {saveError && <span className={styles.saveError}>{saveError}</span>}
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={saving || rebuilding}
            >
              {saving ? "Saving…" : rebuilding ? "Publishing…" : "Save"}
            </button>
          </div>
        </div>

        {/* ── Right: preview ── */}
        <div className={styles.preview}>
          <div className={styles.previewHeader}>
            {(() => {
              const derivedUrl = (() => {
                try { return getFaviconUrl(new URL(htmlUrl || feedUrl).hostname.replace(/^www\./, "")); }
                catch { return ""; }
              })();
              const src = faviconUrl || derivedUrl;
              return src ? (
                <span
                  className={styles.faviconWrap}
                  style={iconBackground ? { background: iconBackground } : undefined}
                >
                  <img src={src} width={20} height={20} alt="" aria-hidden="true" style={{ borderRadius: 3 }} />
                </span>
              ) : null;
            })()}
            <span className={styles.previewTitle}>{title}</span>
            <div className={styles.modeToggle}>
              {PREVIEW_MODES.map(({ mode, label }) => (
                <button
                  key={mode}
                  type="button"
                  className={`${styles.modeBtn} ${previewMode === mode ? styles.modeBtnActive : ""}`}
                  onClick={() => setPreviewMode(mode)}
                  disabled={previewMobile || previewMode === "json" || previewMode === "raw-json"}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className={styles.modeToggle}>
              {[1, 2, 3, 4].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`${styles.modeBtn} ${previewCols === n ? styles.modeBtnActive : ""}`}
                  onClick={() => setPreviewCols(n)}
                  title={`${n} column${n > 1 ? "s" : ""}`}
                  disabled={previewMobile || previewMode === "json" || previewMode === "raw-json"}
                >
                  {n}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`${styles.modeBtn} ${previewMobile ? styles.modeBtnActive : ""}`}
              onClick={() => setPreviewMobile((m) => !m)}
              title="Simulate mobile layout"
              disabled={previewMode === "json" || previewMode === "raw-json"}
            >
              Mobile
            </button>
            <button
              type="button"
              className={`${styles.modeBtn} ${previewMode === "json" ? styles.modeBtnActive : ""}`}
              onClick={() => setPreviewMode((m) => m === "json" ? "thumbnail" : "json")}
              title="View parsed feed JSON"
            >
              JSON
            </button>
            <button
              type="button"
              className={`${styles.modeBtn} ${previewMode === "raw-json" ? styles.modeBtnActive : ""}`}
              onClick={() => {
                if (previewMode === "raw-json") {
                  setPreviewMode("thumbnail");
                } else {
                  setPreviewMode("raw-json");
                  if (!rawFeedData && !rawFeedLoading) fetchRaw(feedUrl);
                }
              }}
              title="View full rss-parser output"
            >
              Raw
            </button>
            {previewMobile && (
              <select
                className={styles.modelSelect}
                value={phoneModel.id}
                onChange={(e) => setPhoneModel(PHONE_MODELS.find((m) => m.id === e.target.value) ?? PHONE_MODELS[0]!)}
              >
                {PHONE_MODELS.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            )}
            <button
              type="button"
              className={styles.modeBtn}
              onClick={() => {
                if (previewMode === "raw-json") fetchRaw(feedUrl);
                else fetchFeedItems(feedUrl);
              }}
              disabled={feedLoading || rawFeedLoading}
              title="Re-fetch feed"
            >
              {feedLoading || rawFeedLoading ? "…" : "↻"}
            </button>
            {htmlUrl && (
              <a href={htmlUrl} target="_blank" rel="noopener noreferrer" className={styles.siteLink}>
                Visit site →
              </a>
            )}
          </div>

          {previewMode === "raw-json" && rawFeedLoading && <p className={styles.previewHint}>Loading raw feed…</p>}
          {previewMode === "raw-json" && rawFeedError && <p className={styles.previewError}>Could not load raw feed: {rawFeedError}</p>}
          {previewMode === "raw-json" && !rawFeedLoading && rawFeedData !== null && (
            <div className={styles.jsonView}>
              <pre className={styles.jsonPre} dangerouslySetInnerHTML={{ __html: syntaxHighlight(JSON.stringify(rawFeedData, null, 2)) }} />
            </div>
          )}
          {previewMode !== "raw-json" && feedLoading && <p className={styles.previewHint}>Loading feed…</p>}
          {previewMode !== "raw-json" && feedError && <p className={styles.previewError}>Could not load feed: {feedError}</p>}
          {previewMode !== "raw-json" && !feedLoading && !feedError && feedItems.length === 0 && (
            <p className={styles.previewHint}>No items found.</p>
          )}
          {!feedLoading && feedItems.length > 0 && previewMode === "json" && (
            <div className={styles.jsonView}>
              <pre
                className={styles.jsonPre}
                dangerouslySetInnerHTML={{
                  __html: syntaxHighlight(JSON.stringify(
                    { feedUrl, channel: feedMeta, itemCount: feedItems.length, items: feedItems },
                    null, 2,
                  )),
                }}
              />
            </div>
          )}
          {!feedLoading && feedItems.length > 0 && previewMode !== "json" && previewMode !== "raw-json" && (() => {
            const dm: DisplayMode =
              previewMode === "gallery" ? "gallery" :
              previewMode === "picture" ? "picture" : "text";
            const snap = toSnapshot(feedItems, sourceId, title, previewMode === "post" || !thumbnails, faviconUrl, iconBackground);
            const colWidths: Record<number, string> = { 1: "100%", 2: "520px", 3: "360px", 4: "270px" };
            if (previewMobile) {
              return (
                <PhoneFrame model={phoneModel}>
                  <FlatView sections={[snap]} readIds={EMPTY_SET} onRead={NOOP} suppressed={EMPTY_SET} mobile={true} />
                </PhoneFrame>
              );
            }
            return (
              <div className={styles.previewPanel} style={{ maxWidth: colWidths[previewCols] }}>
                <SourcePanel section={snap} displayMode={dm} />
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
